import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Heading from '@atlaskit/heading';
import IssuesIcon from '@atlaskit/icon/glyph/issues';
import LockCircleIcon from '@atlaskit/icon/glyph/lock-circle';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import Lozenge from '@atlaskit/lozenge';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, xcss, Stack, Inline, Text } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { type ProjectId, toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { IssueChartSkeleton } from '@atlassian/jira-skeletons/src/ui/cycle-time-breakdown-report/index.tsx';
import ErrorRetry from '@atlassian/jira-software-devops-report-common-error-retry';
import commonMessages from '../../common/messages';
import { secondsToHours, type EChartsEvent } from '../../common/types';
import IssueModal from '../../common/ui/issue-modal';
import { TrendSummary } from '../../common/ui/trend-summary';
import { useFormatDurationFromSeconds } from '../../common/utils/date-range';
import { useSelectedRange } from '../../controllers/selected-date-range';
import { useTableFilterStore } from '../../controllers/top-filter';
import { useViewCycleTimeReportExperienceFail } from '../../services/experience-tracker';
import { useProjectId } from '../../services/get-project';
import { useDateRangeIssueMetrics } from '../../services/period-issue-chart';
import Chart from './chart';
import messages from './messages';

export const IssueChart = () => {
	const projectId: ProjectId | null = useProjectId();
	const [selectedDateRange] = useSelectedRange();
	const [filters] = useTableFilterStore();
	const {
		data: chartData,
		loading,
		error,
		fetch,
	} = useDateRangeIssueMetrics(projectId, selectedDateRange, filters);
	const { formatMessage } = useIntl();
	const formatDurationFromSeconds = useFormatDurationFromSeconds();
	const onViewCycleTimeReportExperienceFail = useViewCycleTimeReportExperienceFail();
	const [selectedIssueKey, setSelectedIssueKey] = useState<unknown>(null);

	const onChartClicked = useCallback(
		(eventData: EChartsEvent) => {
			setSelectedIssueKey(toIssueKey(eventData.name));
		},
		[setSelectedIssueKey],
	);

	const events = useMemo(() => ({ click: onChartClicked }), [onChartClicked]);

	useEffect(() => {
		if (error && chartData == null) {
			onViewCycleTimeReportExperienceFail('IssueTableDataFailed', error);
			fireErrorAnalytics({
				meta: {
					id: 'IssueTableDataFailed',
					packageName: 'jiraSoftwareCycleTimeBreakdownReport',
					teamName: 'fusion-solaris',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [chartData, error, onViewCycleTimeReportExperienceFail]);

	if (error && chartData == null) {
		return (
			<Inline
				xcss={eerrorUIWrapperStyles}
				testId="software-cycle-time-breakdown-report.ui.issue-chart.error-ui-wrapper"
			>
				<ErrorRetry onRetry={fetch} />
			</Inline>
		);
	}
	if (loading || chartData == null || selectedDateRange == null) {
		return <IssueChartSkeleton />;
	}

	const {
		periodCycleTimeInSeconds,
		wholePeriodCycleTimeInSeconds,
		hasEnoughDataForMeaningfulStatistics,
		dataPoints,
	} = chartData;

	const wholePeriodCycleTimeInHours = secondsToHours(wholePeriodCycleTimeInSeconds);

	const duration = formatDurationFromSeconds(periodCycleTimeInSeconds);
	const isEmptyState = dataPoints.length === 0;

	if (chartData.hasPermissionForAllContributingIssues === false && dataPoints.length === 0) {
		return (
			<Stack xcss={issueChartEmptyNoPermissionsStyles}>
				{/* eslint-disable-next-line @atlaskit/design-system/no-legacy-icons */}
				<LockCircleIcon label="" />
				<Text as="span" color="color.text">
					{formatMessage(messages.noPermissions)}
				</Text>
			</Stack>
		);
	}

	return (
		<Box
			xcss={issueChartContainerStyles}
			// Note: this test ID is used in a Pollinator test.
			// eslint-disable-next-line jira/integration/enforce-data-testid-usage, jira/integration/test-id-by-folder-structure
			data-test-id="software-cycle-time-trend-report.ui.issue-chart"
		>
			<Inline xcss={alignVerticalCenterStyles}>
				<Heading size="medium">
					{isEmptyState
						? formatMessage(messages.titleEmpty)
						: `${duration} ${formatMessage(commonMessages.medianCycleTime)}`}
				</Heading>
				{hasEnoughDataForMeaningfulStatistics && (
					<Inline xcss={trendSummaryContainerStyles}>
						<TrendSummary
							currentDuration={periodCycleTimeInSeconds}
							overallDuration={wholePeriodCycleTimeInSeconds}
						/>
					</Inline>
				)}
			</Inline>
			<Inline xcss={descriptionAndLegendContainerStyles}>
				{isEmptyState ? (
					<Lozenge appearance="new">{formatMessage(messages.moreDataRequired)}</Lozenge>
				) : (
					<>
						<Text as="span" size="small" color="color.text">
							<Inline xcss={alignVerticalCenterStyles}>
								<IssuesIcon size="small" label="" />
								<Inline xcss={descriptionTextStyles}>
									{chartData.hasPermissionForAllContributingIssues === true ? (
										formatMessage(messages.numIssuesShipped, {
											numIssuesShipped: dataPoints.length,
										})
									) : (
										<>
											{formatMessage(messages.numIssuesShippedLimitedPermissions, {
												numIssuesShipped: dataPoints.length,
											})}
											<Tooltip content={formatMessage(messages.numIssuesTooltip)} position="right">
												<Box xcss={questionIconStyles}>
													<QuestionCircleIcon size="small" label="" />
												</Box>
											</Tooltip>
										</>
									)}
								</Inline>
							</Inline>
						</Text>
						<Inline xcss={legendContainerStyles}>
							<Inline xcss={legendEntityStyles}>
								<Box xcss={chartSymbolStyles} />
								<Box as="span" xcss={legendLabelMarginLeftStyles}>
									<Text size="small">{formatMessage(messages.issue)}</Text>
								</Box>
							</Inline>
							<Inline xcss={legendEntityStyles}>
								<Text size="small">--</Text>
								<Box as="span" xcss={legendLabelMarginLeftStyles}>
									<Text size="small">{formatMessage(messages.twelveWeekMedian)}</Text>
								</Box>
							</Inline>
						</Inline>
					</>
				)}
			</Inline>
			<Box xcss={graphWrappeStyles}>
				<Chart
					dataPoints={dataPoints}
					dateRange={selectedDateRange}
					medianInSeconds={
						hasEnoughDataForMeaningfulStatistics ? wholePeriodCycleTimeInSeconds : null
					}
					maxValueInSeconds={wholePeriodCycleTimeInSeconds * 4}
					displayUnits={wholePeriodCycleTimeInHours >= 24 ? 'days' : 'hours'}
					onEvents={events}
				/>
			</Box>
			<ModalTransition>
				{/* @ts-expect-error - TS2769 - No overload matches this call. */}
				{selectedIssueKey && (
					<IssueModal
						// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string'.
						issueKey={selectedIssueKey}
						onClose={() => {
							setSelectedIssueKey(null);
						}}
						onIssueKeyChange={({ toIssueKey: newIssueKey }) => {
							setSelectedIssueKey(newIssueKey);
						}}
					/>
				)}
			</ModalTransition>
		</Box>
	);
};

const issueChartContainerStyles = xcss({
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
});

const issueChartEmptyNoPermissionsStyles = xcss({
	width: '100%',
	height: '424px',
	justifyContent: 'center',
	alignItems: 'center',
});

const alignVerticalCenterStyles = xcss({
	alignItems: 'center',
});

const descriptionTextStyles = xcss({
	marginLeft: 'space.050',
	alignItems: 'center',
});

const graphWrappeStyles = xcss({
	height: '376px',
});

const descriptionAndLegendContainerStyles = xcss({
	justifyContent: 'space-between',
	marginTop: 'space.100',
});

const legendContainerStyles = xcss({
	alignItems: 'center',
});

const legendEntityStyles = xcss({
	alignItems: 'center',
	paddingLeft: 'space.100',
	lineHeight: '11px',
});

const legendLabelMarginLeftStyles = xcss({
	marginLeft: 'space.050',
});

const trendSummaryContainerStyles = xcss({
	marginLeft: 'space.200',
	alignItems: 'center',
});

const eerrorUIWrapperStyles = xcss({
	height: '424px',
	justifyContent: 'center',
	alignItems: 'center',
});

const questionIconStyles = xcss({
	marginLeft: 'space.050',
	fill: 'color.icon.inverse',
	color: 'color.text',
	cursor: 'pointer',
	paddingTop: 'space.025',
});

const chartSymbolStyles = xcss({
	width: '8px',
	height: '8px',
	borderRadius: '100%',
	backgroundColor: 'color.background.accent.blue.subtle',
});
